import type {
	useIncomingLinksQuery$data,
	useIncomingLinksQuery$variables,
} from './__generated__/useIncomingLinksQuery.graphql.ts';
import type { IncomingLinksPagePhase } from './incomingLinksAnalytics.ts';
import {
	startIncomingLinksPageObservation,
	type IncomingLinksPageObservation,
	type IncomingLinksPageEventFactory,
} from './incomingLinksPageObservation.ts';

export type IncomingLinksPageResult = {
	data: useIncomingLinksQuery$data;
	error?: { graphQLErrors?: readonly unknown[]; networkError?: Error | null };
	errors?: readonly unknown[];
};

export type IncomingLinksPageFetchMore = (options: {
	variables: Pick<useIncomingLinksQuery$variables, 'first' | 'after'>;
	updateQuery: (
		previousResult: useIncomingLinksQuery$data,
		options: { fetchMoreResult?: useIncomingLinksQuery$data },
	) => useIncomingLinksQuery$data;
}) => Promise<IncomingLinksPageResult>;

export type FetchIncomingLinksPageOptions = {
	fetchMore: IncomingLinksPageFetchMore;
	createEvent: IncomingLinksPageEventFactory;
	pageSize: number;
	after: string;
	phase: IncomingLinksPagePhase;
	observation: IncomingLinksPageObservation;
};

// `updateQuery` merge shared by both paged fetches (the initial catch-up loop and
// `loadMore`): append the incoming page's edges after the ones already in the cache
// entry, and adopt the new page's `pageInfo` so the next fetch reads the latest
// cursor. A response with no connection leaves the cache untouched.
const appendNextPage = (
	previousResult: useIncomingLinksQuery$data,
	fetchMoreResult: useIncomingLinksQuery$data | undefined,
): useIncomingLinksQuery$data => {
	const nextConnection = fetchMoreResult?.graphStore_cypherQueryV2;
	if (!nextConnection) {
		return previousResult;
	}
	const previousEdges = previousResult?.graphStore_cypherQueryV2?.edges ?? [];
	return {
		...previousResult,
		graphStore_cypherQueryV2: {
			...nextConnection,
			edges: [...previousEdges, ...nextConnection.edges],
			pageInfo: nextConnection.pageInfo,
		},
	};
};

export async function fetchIncomingLinksPage({
	fetchMore,
	createEvent,
	pageSize,
	after,
	phase,
	observation,
}: FetchIncomingLinksPageOptions): Promise<IncomingLinksPageResult> {
	const pageAnalytics = startIncomingLinksPageObservation({
		after,
		phase,
		observation,
		createEvent,
	});
	try {
		const result = await fetchMore({
			variables: { first: pageSize, after },
			updateQuery: (previousResult, { fetchMoreResult }) => {
				pageAnalytics.captureRawPage(fetchMoreResult);
				return appendNextPage(previousResult, fetchMoreResult);
			},
		});
		pageAnalytics.resolved(result);
		return result;
	} catch (error) {
		pageAnalytics.rejected(error);
		throw error;
	}
}
